import type { ModelReasoningEffortSetting } from "./types";

import {
  ANTHROPIC_FABLE_5_1_MODEL_REF,
  ANTHROPIC_FABLE_5_MODEL_REF,
} from "./anthropic";
import {
  AZURE_FOUNDRY_PROVIDER_PREFIX,
  DEEPSEEK_V4_PRO_AZURE_MODEL_REF,
  KIMI_K26_AZURE_MODEL_REF,
} from "./azureFoundry";
import {
  BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_REF,
  BEDROCK_ANTHROPIC_FABLE_5_MODEL_REF,
} from "./bedrockAnthropic";
import {
  BEDROCK_OPENAI_GPT_5_4_MODEL_REF,
  BEDROCK_OPENAI_GPT_5_5_MODEL_REF,
  BEDROCK_OPENAI_GPT_5_6_SOL_MODEL_REF,
} from "./bedrockOpenAi";
import { BEDROCK_CONVERSE_FABLE_5_1_MODEL_REF } from "./claudeFable";
import { BEDROCK_CONVERSE_OPUS_5_5_MODEL_REF } from "./claudeOpus";
import { GLM_52_FAST_MODEL_REF, GLM_52_MODEL_REF } from "./glmOpenRouter";
import { GROK_45_MODEL_REF } from "./grokOpenRouter";
import { KIMI_K3_MODEL_REF } from "./kimiK3OpenRouter";
import { MINIMAX_M27_MODEL_REF, MINIMAX_M3_MODEL_REF } from "./minimaxM27";
import {
  OPENAI_GPT_5_6_SOL_MODEL_REF,
  OPENAI_GPT_6_ASTRA_MODEL_REF,
  OPENAI_GPT_6_SOL_MODEL_REF,
} from "./openAi";

export interface ReasoningEffortOption {
  label: string;
  value: ModelReasoningEffortSetting;
}

const EFFORT_NONE: ReasoningEffortOption = { label: "none", value: "none" };
const EFFORT_MINIMAL: ReasoningEffortOption = {
  label: "minimal",
  value: "minimal",
};
const EFFORT_LOW: ReasoningEffortOption = { label: "low", value: "low" };
const EFFORT_MEDIUM: ReasoningEffortOption = {
  label: "medium",
  value: "medium",
};
const EFFORT_HIGH: ReasoningEffortOption = { label: "high", value: "high" };
const EFFORT_XHIGH: ReasoningEffortOption = { label: "xhigh", value: "xhigh" };
const EFFORT_MAX: ReasoningEffortOption = { label: "max", value: "max" };

/**
 * The @ai-sdk/anthropic provider-options schema rejects "xhigh", so
 * anthropic-direct and bedrock-anthropic models cap out at "max".
 */
const ANTHROPIC_SDK_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
  EFFORT_MAX,
];
const CONVERSE_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
  EFFORT_XHIGH,
  EFFORT_MAX,
];
const CONVERSE_46_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
  EFFORT_MAX,
];
const OPENAI_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_NONE,
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
  EFFORT_XHIGH,
];
const OPENAI_56_SOL_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_NONE,
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
  EFFORT_XHIGH,
  EFFORT_MAX,
];
const GEMINI_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_MINIMAL,
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
];
const OPENAI_6_ASTRA_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
  EFFORT_XHIGH,
  EFFORT_MAX,
];
const GEMINI_31_PRO_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
];
const KIMI_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_LOW,
  EFFORT_HIGH,
  EFFORT_MAX,
];
const GLM_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_HIGH,
  EFFORT_XHIGH,
];
const GROK_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
];
const DEEPSEEK_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_LOW,
  EFFORT_HIGH,
  EFFORT_MAX,
];
const AZURE_DEEPSEEK_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_NONE,
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
  EFFORT_XHIGH,
  EFFORT_MAX,
];
const NOVA_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
];
const ALL_EFFORTS: readonly ReasoningEffortOption[] = [
  EFFORT_NONE,
  EFFORT_MINIMAL,
  EFFORT_LOW,
  EFFORT_MEDIUM,
  EFFORT_HIGH,
  EFFORT_XHIGH,
  EFFORT_MAX,
];
const NO_EFFORTS: readonly ReasoningEffortOption[] = [];

/**
 * Effort sets mirror reasoningEffortAdapter (arti-provider): every model lists
 * only the values it accepts as-is — nothing is aliased or clamped.
 */
export const MODEL_REASONING_EFFORT_CAPABILITIES: Record<
  string,
  readonly ReasoningEffortOption[]
> = {
  [ANTHROPIC_FABLE_5_MODEL_REF]: ANTHROPIC_SDK_EFFORTS,
  [ANTHROPIC_FABLE_5_1_MODEL_REF]: ANTHROPIC_SDK_EFFORTS,
  "anthropic/claude-opus-4-8": ANTHROPIC_SDK_EFFORTS,
  [BEDROCK_ANTHROPIC_FABLE_5_MODEL_REF]: ANTHROPIC_SDK_EFFORTS,
  [BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_REF]: ANTHROPIC_SDK_EFFORTS,
  "bedrock/global.anthropic.claude-fable-5": CONVERSE_EFFORTS,
  [BEDROCK_CONVERSE_FABLE_5_1_MODEL_REF]: CONVERSE_EFFORTS,
  "bedrock/global.anthropic.claude-opus-4-6-v1": CONVERSE_46_EFFORTS,
  "bedrock/global.anthropic.claude-opus-4-7": CONVERSE_EFFORTS,
  "bedrock/global.anthropic.claude-opus-4-8": CONVERSE_EFFORTS,
  "bedrock/global.anthropic.claude-opus-5": CONVERSE_EFFORTS,
  [BEDROCK_CONVERSE_OPUS_5_5_MODEL_REF]: CONVERSE_EFFORTS,
  "bedrock/global.anthropic.claude-sonnet-4-6": CONVERSE_46_EFFORTS,
  "bedrock/global.anthropic.claude-sonnet-5": CONVERSE_EFFORTS,
  [BEDROCK_OPENAI_GPT_5_6_SOL_MODEL_REF]: OPENAI_56_SOL_EFFORTS,
  [BEDROCK_OPENAI_GPT_5_5_MODEL_REF]: OPENAI_EFFORTS,
  [BEDROCK_OPENAI_GPT_5_4_MODEL_REF]: OPENAI_EFFORTS,
  [OPENAI_GPT_5_6_SOL_MODEL_REF]: OPENAI_56_SOL_EFFORTS,
  [OPENAI_GPT_6_ASTRA_MODEL_REF]: OPENAI_6_ASTRA_EFFORTS,
  // GPT-6 Sol publishes the same set as GPT-5.6 Sol: none through max.
  [OPENAI_GPT_6_SOL_MODEL_REF]: OPENAI_56_SOL_EFFORTS,
  "google/gemini-3.1-pro": GEMINI_31_PRO_EFFORTS,
  "google/gemini-3.5-flash": GEMINI_EFFORTS,
  "openai/gpt-5.5": OPENAI_EFFORTS,
  [KIMI_K3_MODEL_REF]: KIMI_EFFORTS,
  [GLM_52_MODEL_REF]: GLM_EFFORTS,
  [GLM_52_FAST_MODEL_REF]: GLM_EFFORTS,
  [GROK_45_MODEL_REF]: GROK_EFFORTS,
  "openrouter/deepseek/deepseek-v4-pro": DEEPSEEK_EFFORTS,
  [DEEPSEEK_V4_PRO_AZURE_MODEL_REF]: AZURE_DEEPSEEK_EFFORTS,
  "openrouter/xiaomi/mimo-v2.5-pro": NO_EFFORTS,
  "vertex/gemini-3.1-pro": GEMINI_31_PRO_EFFORTS,
  [MINIMAX_M27_MODEL_REF]: NO_EFFORTS,
  [MINIMAX_M3_MODEL_REF]: NO_EFFORTS,
  [KIMI_K26_AZURE_MODEL_REF]: NO_EFFORTS,
};

export function supportedReasoningEfforts(
  modelRef: string | null | undefined,
): readonly ReasoningEffortOption[] {
  const trimmed = modelRef?.trim();
  if (!trimmed) {
    // The runtime default model (Claude Sonnet 4.6 via bedrock Converse)
    // accepts low/medium/high/max.
    return CONVERSE_46_EFFORTS;
  }
  const exact = MODEL_REASONING_EFFORT_CAPABILITIES[trimmed];
  if (exact) {
    return exact;
  }
  if (trimmed.startsWith(`${AZURE_FOUNDRY_PROVIDER_PREFIX}/`)) {
    return NO_EFFORTS;
  }
  if (trimmed.startsWith("bedrock-openai/")) {
    return OPENAI_EFFORTS;
  }
  if (trimmed.startsWith("bedrock-anthropic/")) {
    return ANTHROPIC_SDK_EFFORTS;
  }
  // Nova accepts maxReasoningEffort low/medium/high (AWS Bedrock Nova docs).
  if (
    trimmed.startsWith("bedrock/us.amazon.nova") ||
    trimmed.startsWith("bedrock/amazon.nova")
  ) {
    return NOVA_EFFORTS;
  }
  if (trimmed.startsWith("google/") || trimmed.startsWith("vertex/")) {
    return GEMINI_EFFORTS;
  }
  if (trimmed.startsWith("openai/")) {
    return OPENAI_EFFORTS;
  }
  if (trimmed.startsWith("openrouter/")) {
    return ALL_EFFORTS;
  }
  // Only the Fable models and Opus 4.8 are wired for effort on the anthropic-direct
  // path; every other anthropic/ ref drops the effort at runtime.
  if (trimmed.startsWith("anthropic/")) {
    return NO_EFFORTS;
  }
  // Unknown refs stay unrestricted so legacy rows are not silently narrowed.
  return ALL_EFFORTS;
}

export function isSupportedReasoningEffort(
  modelRef: string | null | undefined,
  effort: ModelReasoningEffortSetting | null | undefined,
): boolean {
  if (!effort) {
    return true;
  }
  return supportedReasoningEfforts(modelRef).some(
    (option) => option.value === effort,
  );
}
